import {
    FETCH_CART_SUCCESS,
    REMOVE_ITEM_SUCCESS,
    REMOVE_ITEMS_SUCCESS,
    UNDO_REMOVE_ITEM_SUCCESS,
    MOVE_TO_WISHLIST_SUCCESS,
    MOVE_SOLD_OUT_TO_WISHLIST_SUCCESS,
    UNDO_MOVE_TO_WISHLIST_SUCCESS,
    UNDO_MOVE_SOLD_OUT_TO_WISHLIST_SUCCESS,
    DISMISS_REMOVE_ITEM,
    ADD_PROMOTION_SUCCESS,
    REMOVE_PROMOTION_SUCCESS,
    SET_QUANTITY_SUCCESS,
    SET_SHIPPING_METHOD_SUCCESS,
    SET_SHIPPING_ADDRESS_SUCCESS,
    SET_SHIPPING_ADDRESS_PARTIAL_ERROR,
    SET_PAYMENT_METHOD_SUCCESS,
    ADD_ADDRESS_SUCCESS,
    EDIT_ADDRESS_SUCCESS,
    CLEAR_CART,
    CHECKOUT_SUCCESS,
    FAST_CHECKOUT_SUCCESS,
    UPDATE_LINE_ITEM_SUCCESS,
    SET_PACKAGING_OPTION_SUCCESS,
    CLEAR_RECTIFICATION,
    SET_RECTIFICATION,
    SET_AP_ADDRESS_DETAILS,
} from '../constants/ActionTypes'
import { RESERVATION_NUMBER_INVALID } from '../actions/cartRectification'
import { rectifyItemsByOrderMessage } from '../utils/rectifyItemsByOrderMessage'
import GenericAction from './types/genericAction.types'
import { OrderItem, OrderItemIncludingRemoved, ShoppingCart } from 'app/types/common-types'
import { LineItem, StoreState } from 'app/state/types'

type CartActionType =
    | typeof FETCH_CART_SUCCESS
    | typeof REMOVE_ITEM_SUCCESS
    | typeof REMOVE_ITEMS_SUCCESS
    | typeof UNDO_REMOVE_ITEM_SUCCESS
    | typeof MOVE_TO_WISHLIST_SUCCESS
    | typeof MOVE_SOLD_OUT_TO_WISHLIST_SUCCESS
    | typeof UNDO_MOVE_TO_WISHLIST_SUCCESS
    | typeof UNDO_MOVE_SOLD_OUT_TO_WISHLIST_SUCCESS
    | typeof DISMISS_REMOVE_ITEM
    | typeof ADD_PROMOTION_SUCCESS
    | typeof REMOVE_PROMOTION_SUCCESS
    | typeof SET_QUANTITY_SUCCESS
    | typeof SET_SHIPPING_METHOD_SUCCESS
    | typeof SET_SHIPPING_ADDRESS_SUCCESS
    | typeof SET_SHIPPING_ADDRESS_PARTIAL_ERROR
    | typeof SET_PAYMENT_METHOD_SUCCESS
    | typeof ADD_ADDRESS_SUCCESS
    | typeof EDIT_ADDRESS_SUCCESS
    | typeof CLEAR_CART
    | typeof CHECKOUT_SUCCESS
    | typeof FAST_CHECKOUT_SUCCESS
    | typeof UPDATE_LINE_ITEM_SUCCESS
    | typeof SET_PACKAGING_OPTION_SUCCESS
    | typeof CLEAR_RECTIFICATION
    | typeof SET_RECTIFICATION
    | typeof SET_AP_ADDRESS_DETAILS

type ActionPayload = {
    payload: { shoppingCart: ShoppingCart; orderId: number | string }
    orderItemId: number
    quantity: number
    giftListItemID: number | string
    alreadyInWishlist: boolean
    partNumber: string
    rectificationType: string
}

type CartAction = GenericAction<CartActionType, ActionPayload>

type CartState = StoreState & {
    removedItems: LineItem[]
    orderItemIncludingRemoved: OrderItemIncludingRemoved[]
    orderItem: OrderItem[]
}

type NextState = ShoppingCart | { shoppingCart: ShoppingCart; orderId: string | number }

export default function cart(state: CartState = {} as CartState, action: CartAction) {
    switch (action.type) {
        case SET_SHIPPING_METHOD_SUCCESS:
        case SET_SHIPPING_ADDRESS_SUCCESS:
        case SET_SHIPPING_ADDRESS_PARTIAL_ERROR:
        case SET_PAYMENT_METHOD_SUCCESS:
        case CHECKOUT_SUCCESS:
        case FAST_CHECKOUT_SUCCESS:
        case ADD_ADDRESS_SUCCESS:
        case EDIT_ADDRESS_SUCCESS:
        case REMOVE_ITEMS_SUCCESS:
        case SET_PACKAGING_OPTION_SUCCESS: {
            const orderItemIncludingRemoved = getOrderItemIncludingRemoved(state, action.payload.shoppingCart)
            const newCart = action.payload.shoppingCart
            // x_cvvRequired is not returned if true, which confuses redux
            if (!('x_cvvRequired' in newCart)) {
                newCart.x_cvvRequired = true
            }
            // if orderMessages were cleared in newCart, we also clear them in the updated state
            if (!('orderMessages' in newCart)) delete state.orderMessages
            return { ...state, ...newCart, orderItemIncludingRemoved, donationOrderId: action.payload.orderId }
        }
        case ADD_PROMOTION_SUCCESS:
        case REMOVE_PROMOTION_SUCCESS:
        case UNDO_REMOVE_ITEM_SUCCESS:
        case UNDO_MOVE_TO_WISHLIST_SUCCESS:
        case UPDATE_LINE_ITEM_SUCCESS: {
            const orderItemIncludingRemoved = getOrderItemIncludingRemoved(state, action.payload.shoppingCart, action.orderItemId)
            return { ...state, ...action.payload.shoppingCart, orderItemIncludingRemoved, removedItems: state.removedItems }
        }
        case SET_QUANTITY_SUCCESS: {
            const orderItemIncludingRemoved = getOrderItemIncludingRemoved(
                state,
                action.payload.shoppingCart,
                null,
                (orderItemId: number) => action.quantity === 0 && action.orderItemId === orderItemId
            )
            return { ...state, ...action.payload.shoppingCart, orderItemIncludingRemoved, removedItems: state.removedItems }
        }
        case REMOVE_ITEM_SUCCESS: {
            const orderItemIncludingRemoved = getOrderItemIncludingRemoved(
                state,
                action.payload.shoppingCart,
                null,
                (orderItemId: number) => action.orderItemId === orderItemId
            )
            return { ...state, ...action.payload.shoppingCart, orderItemIncludingRemoved, removedItems: state.removedItems }
        }
        case MOVE_TO_WISHLIST_SUCCESS: {
            const orderItemIncludingRemoved = getOrderItemIncludingRemoved(
                state,
                action.payload.shoppingCart,
                null,
                (orderItemId: number) => action.orderItemId === orderItemId,
                {
                    giftListItemID: action.giftListItemID,
                    alreadyInWishlist: action.alreadyInWishlist,
                }
            )
            return { ...state, ...action.payload.shoppingCart, orderItemIncludingRemoved, removedItems: state.removedItems }
        }
        case MOVE_SOLD_OUT_TO_WISHLIST_SUCCESS: {
            const removedItems = state.removedItems.map((item) => {
                if (item.partNumber !== action.partNumber) return item
                return { ...item, giftListItemID: action.giftListItemID, alreadyInWishlist: action.alreadyInWishlist, removed: true }
            })
            return { ...state, removedItems }
        }
        case UNDO_MOVE_SOLD_OUT_TO_WISHLIST_SUCCESS: {
            const removedItems = state.removedItems.map((item) => {
                if (item.partNumber !== action.partNumber) return item
                /* eslint no-unused-vars: ["error", { "varsIgnorePattern": "giftListItemID||alreadyInWishlist||removed" }] */
                const { giftListItemID, alreadyInWishlist, removed, ...undoneItem } = item
                return undoneItem
            })
            return { ...state, removedItems }
        }
        case FETCH_CART_SUCCESS: {
            // TODO fix this as soon as BE sends back removedItems as an empty array
            const nextState = action.payload
            const orderItemIncludingRemoved = getOrderItemIncludingRemoved(null, nextState)
            return { ...nextState, orderItemIncludingRemoved }
        }
        case CLEAR_CART: {
            return {}
        }
        case DISMISS_REMOVE_ITEM:
            return { ...state, orderItemIncludingRemoved: state.orderItemIncludingRemoved.filter(({ partNumber }) => action.partNumber !== partNumber) }
        case CLEAR_RECTIFICATION:
            return { ...state, rectification: false }
        case SET_RECTIFICATION: {
            const { type, payload, rectificationType, ...rest } = action
            const { shoppingCart = {} as ShoppingCart } = payload

            const rectifiedQtyItems = rectifyItemsByOrderMessage(shoppingCart?.orderMessages)
            const rectifiedQtyItemsList = rectifiedQtyItems?.map((item) => item.orderItemId)

            const isReservationRectification = rectificationType === RESERVATION_NUMBER_INVALID
            const rectifiedPartNumbers = shoppingCart.removedItems?.map((item) => item.partNumber) || []
            const orderItemsRemoved =
                state.orderItem
                    ?.filter((item) => rectifiedPartNumbers.find((partNumber) => !isReservationRectification && partNumber === item.partNumber))
                    .map((item) => item.orderItemId) || []

            const orderItemIncludingRemoved = getOrderItemIncludingRemoved(
                state,
                shoppingCart,
                null,
                (orderItemId: number) => orderItemsRemoved.indexOf(orderItemId) > -1
            )
            const rectifiedShoppingCartRemovedItems = rectifiedQtyItemsList?.length
                ? shoppingCart.removedItems?.filter((item) => !rectifiedQtyItemsList.includes(Number(item.partNumber)))
                : shoppingCart.removedItems
            const rectifiedStateRemovedItems = rectifiedQtyItemsList?.length
                ? state.removedItems.filter((item) => !rectifiedQtyItemsList.includes(Number(item.partNumber)))
                : state.removedItems
            return {
                ...shoppingCart,
                rectifiedItemsList: rectifiedQtyItemsList,
                rectification: { ...rest, rectificationType },
                removedItems: rectifiedShoppingCartRemovedItems || rectifiedStateRemovedItems,
                orderItemIncludingRemoved,
            }
        }
        case SET_AP_ADDRESS_DETAILS: {
            const { payload } = action
            return {
                ...state,
                applePayAddressDetails: payload,
            }
        }
        default:
            return state
    }
}

// TODO: Add tests
function getOrderItemIncludingRemoved(state: CartState | null, nextState: NextState, changedProductId?: number | null, canAddToRemoved?: any, meta: any = {}) {
    const orderItemIncludingRemoved = [] as OrderItemIncludingRemoved[]
    if (state && state.orderItemIncludingRemoved) {
        for (let i = 0; i < state.orderItemIncludingRemoved.length; i++) {
            const item = state.orderItemIncludingRemoved[i]
            if (canAddToRemoved && canAddToRemoved(item.orderItemId)) {
                const removedOrderItem = state.orderItem.find(({ orderItemId }) => orderItemId === item.orderItemId)
                orderItemIncludingRemoved.push({ ...removedOrderItem, ...meta, removed: true })
            } else if (
                nextState &&
                'orderItem' in nextState &&
                nextState.orderItem?.find(({ orderItemId, partNumber }) => orderItemId === item.orderItemId || (!orderItemId && partNumber === item.partNumber))
            ) {
                orderItemIncludingRemoved.push({ orderItemId: item.orderItemId })
            } else if (item.removed && item.orderItemId !== changedProductId) {
                orderItemIncludingRemoved.push(item)
            }
        }
    }
    if (nextState && 'orderItem' in nextState) {
        for (let i = 0; i < nextState.orderItem?.length; i++) {
            const item = nextState.orderItem?.[i]
            if (!orderItemIncludingRemoved.find(({ orderItemId }) => orderItemId === item.orderItemId)) {
                orderItemIncludingRemoved.push({ orderItemId: item.orderItemId })
            }
        }
    }
    return orderItemIncludingRemoved
}
